/**
 * 大会対戦表/ブラケット画面シーン
 *
 * 総当たり戦: 対戦成績表 + 順位表を表示
 * トーナメント戦: ブラケット図を表示
 *
 * 次の試合に対して:
 * - 自分の試合 → 「バトル開始」
 * - CPU同士 → 「観戦」「スキップ」
 * - 全試合完了 → 「結果を見る」
 */
import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import {
  TournamentState,
  TournamentMode,
  TournamentPhase,
  TournamentMatch,
} from '../types/Tournament';
import { GameMode } from '../types/GameMode';
import { BattleResultType } from '../types/BattleState';
import { MONSTER_DATABASE } from '../constants/monsters';
import { FINAL_MONSTER_DATABASE } from '../constants/monsterStats';
import {
  getNextMatch,
  isPlayerMatch,
  recordMatchResult,
  advanceEliminationBracket,
} from '../tournament/tournamentManager';
import { simulateHeadlessBattle } from '../tournament/headlessBattle';
import { calculateRoundRobinStandings } from '../tournament/standingsCalculator';
import {
  TOURNAMENT_BRACKET_LAYOUT as LAYOUT,
  TOURNAMENT_BRACKET_LABELS as LABELS,
} from './tournamentBracketConfig';
import { BackgroundImageKey } from '../constants/imageKeys';

interface TournamentBracketSceneData {
  tournamentState: TournamentState;
}

export class TournamentBracketScene extends BaseScene {
  private tournamentState!: TournamentState;
  private uiElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super(SceneKey.TOURNAMENT_BRACKET);
  }

  create(data?: TournamentBracketSceneData): void {
    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE).setOrigin(0.5);
    }

    if (!data?.tournamentState) {
      this.transitionTo(SceneKey.TOURNAMENT_SETUP);
      return;
    }

    this.tournamentState = data.tournamentState;
    this.renderUI();
  }

  private clearUI(): void {
    this.uiElements.forEach((el) => el.destroy());
    this.uiElements = [];
  }

  private renderUI(): void {
    this.clearUI();

    const isRoundRobin = this.tournamentState.mode === TournamentMode.ROUND_ROBIN;
    const title = isRoundRobin ? LABELS.roundRobinTitle : LABELS.eliminationTitle;

    // Title
    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, LAYOUT.titleY, title, {
          fontSize: '28px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5),
    );

    // Progress
    const completedCount = this.tournamentState.matches.filter((m) => m.result).length;
    const totalCount = this.tournamentState.matches.length;
    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, LAYOUT.matchInfoY, LABELS.matchProgress(completedCount, totalCount), {
          fontSize: '16px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5),
    );

    if (isRoundRobin) {
      this.renderRoundRobinTable();
    } else {
      this.renderEliminationBracket();
    }

    this.renderActionButtons();
  }

  private getParticipantName(participantId: string): string {
    if (!participantId) return '---';
    const participant = this.tournamentState.participants.find((p) => p.id === participantId);
    if (!participant) return '---';
    const monster = MONSTER_DATABASE.find((m) => m.id === participant.monsterId);
    return monster ? monster.name : participant.monsterId;
  }

  private renderRoundRobinTable(): void {
    const standings = calculateRoundRobinStandings(this.tournamentState);
    const startX = 50;
    const startY = LAYOUT.tableStartY;
    const rowH = LAYOUT.tableRowHeight;
    const colWidths = [40, 100, 40, 40, 40, 50];

    // Header
    const headers = [LABELS.rank, LABELS.name, LABELS.win, LABELS.lose, LABELS.draw, LABELS.pts];
    let x = startX;
    headers.forEach((h, i) => {
      this.uiElements.push(
        this.add.text(x, startY, h, {
          fontSize: '14px',
          color: '#ffdd44',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 3,
        }),
      );
      x += colWidths[i];
    });

    // Rows
    standings.forEach((p, rank) => {
      const y = startY + (rank + 1) * rowH;
      const isPlayer = p.isPlayer;
      const color = isPlayer ? '#44ff44' : '#ffffff';
      x = startX;

      const values = [
        `${rank + 1}`,
        this.getParticipantName(p.id),
        `${p.wins}`,
        `${p.losses}`,
        `${p.draws}`,
        `${p.points}`,
      ];

      values.forEach((v, i) => {
        this.uiElements.push(
          this.add.text(x, y, v, {
            fontSize: '13px',
            color,
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
            stroke: '#000000',
            strokeThickness: 3,
          }),
        );
        x += colWidths[i];
      });
    });

    // Match results on right side
    this.renderMatchList();
  }

  private renderMatchList(): void {
    const startX = 400;
    const startY = LAYOUT.tableStartY;
    const rowH = 20;

    this.uiElements.push(
      this.add.text(startX, startY, '対戦結果', {
        fontSize: '14px',
        color: '#ffdd44',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      }),
    );

    const completedMatches = this.tournamentState.matches.filter((m) => m.result);
    const displayMatches = completedMatches.slice(-15); // Show last 15

    displayMatches.forEach((m, i) => {
      const y = startY + (i + 1) * rowH;
      const p1Name = this.getParticipantName(m.participant1Id);
      const p2Name = this.getParticipantName(m.participant2Id);
      let resultMark = '△';
      if (m.result?.winnerId === m.participant1Id) resultMark = '○-●';
      else if (m.result?.winnerId === m.participant2Id) resultMark = '●-○';

      this.uiElements.push(
        this.add.text(startX, y, `${p1Name} ${resultMark} ${p2Name}`, {
          fontSize: '12px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        }),
      );
    });
  }

  private renderEliminationBracket(): void {
    const startX = LAYOUT.bracketStartX;
    const startY = LAYOUT.bracketStartY;

    // Render by round
    for (let round = 0; round <= 2; round++) {
      const roundMatches = this.tournamentState.matches.filter((m) => m.round === round);
      const roundName = LABELS.roundNames[round];

      const roundX = startX + round * LAYOUT.bracketRoundSpacing;
      const matchesInRound = roundMatches.length;
      const totalHeight = GAME_HEIGHT - startY - 100;
      const spacing = totalHeight / matchesInRound;

      // Round label
      this.uiElements.push(
        this.add.text(roundX, startY - 20, roundName, {
          fontSize: '14px',
          color: '#ffdd44',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 3,
        }),
      );

      roundMatches.forEach((match, i) => {
        const y = startY + i * spacing + spacing / 2;
        this.renderBracketMatch(roundX, y, match);
      });
    }
  }

  private renderBracketMatch(x: number, y: number, match: TournamentMatch): void {
    const w = LAYOUT.bracketMatchWidth;
    const h = LAYOUT.bracketMatchHeight;

    // Background
    const hasResult = !!match.result;
    const bgColor = hasResult ? 0x334433 : 0x333344;
    this.uiElements.push(
      this.add.rectangle(x + w / 2, y, w, h, bgColor).setOrigin(0.5),
    );

    const p1Name = this.getParticipantName(match.participant1Id);
    const p2Name = this.getParticipantName(match.participant2Id);

    let p1Color = '#ffffff';
    let p2Color = '#ffffff';
    if (match.result) {
      if (match.result.winnerId === match.participant1Id) {
        p1Color = '#44ff44';
        p2Color = '#ff4444';
      } else if (match.result.winnerId === match.participant2Id) {
        p1Color = '#ff4444';
        p2Color = '#44ff44';
      } else {
        p1Color = '#ffff44';
        p2Color = '#ffff44';
      }
    }

    this.uiElements.push(
      this.add.text(x + 5, y - 10, p1Name, {
        fontSize: '12px',
        color: p1Color,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      }),
    );

    this.uiElements.push(
      this.add.text(x + 5, y + 2, p2Name, {
        fontSize: '12px',
        color: p2Color,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      }),
    );
  }

  private renderActionButtons(): void {
    const nextMatch = getNextMatch(this.tournamentState);

    if (!nextMatch) {
      // All matches done
      const btn = this.add
        .text(GAME_WIDTH / 2, LAYOUT.actionButtonY, LABELS.viewResult, {
          fontSize: '28px',
          color: '#ffdd44',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => btn.setScale(1.1));
      btn.on('pointerout', () => btn.setScale(1.0));
      btn.on('pointerdown', () => {
        this.transitionTo(SceneKey.TOURNAMENT_RESULT, {
          tournamentState: this.tournamentState,
        });
      });
      this.uiElements.push(btn);
      return;
    }

    // Show next match info
    const p1Name = this.getParticipantName(nextMatch.participant1Id);
    const p2Name = this.getParticipantName(nextMatch.participant2Id);
    const matchText = `${LABELS.nextMatch}: ${p1Name} ${LABELS.vs} ${p2Name}`;

    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, LAYOUT.actionButtonY - 40, matchText, {
          fontSize: '18px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5),
    );

    if (isPlayerMatch(this.tournamentState, nextMatch)) {
      // Player match: single "Battle" button
      this.createActionButton(GAME_WIDTH / 2, LAYOUT.actionButtonY, LABELS.battle, '#44ff44', () => {
        this.startBattle(nextMatch, false);
      });
    } else {
      // CPU match: "Watch" and "Skip" buttons
      this.createActionButton(
        GAME_WIDTH / 2 - LAYOUT.actionButtonSpacing / 2,
        LAYOUT.actionButtonY,
        LABELS.watch,
        '#4488ff',
        () => this.startBattle(nextMatch, true),
      );

      this.createActionButton(
        GAME_WIDTH / 2 + LAYOUT.actionButtonSpacing / 2,
        LAYOUT.actionButtonY,
        LABELS.skip,
        '#888888',
        () => this.skipMatch(nextMatch),
      );
    }
  }

  private createActionButton(x: number, y: number, label: string, color: string, action: () => void): void {
    const btn = this.add
      .text(x, y, label, {
        fontSize: '24px',
        color,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    btn.on('pointerover', () => btn.setScale(1.1));
    btn.on('pointerout', () => btn.setScale(1.0));
    btn.on('pointerdown', action);
    this.uiElements.push(btn);
  }

  private startBattle(match: TournamentMatch, isSpectator: boolean): void {
    const p1 = this.tournamentState.participants.find((p) => p.id === match.participant1Id);
    const p2 = this.tournamentState.participants.find((p) => p.id === match.participant2Id);
    if (!p1 || !p2) return;

    const gameMode = isSpectator
      ? GameMode.SPECTATOR
      : (this.tournamentState.mode === TournamentMode.ROUND_ROBIN
          ? GameMode.TOURNAMENT_ROUND_ROBIN
          : GameMode.TOURNAMENT_ELIMINATION);

    // For player matches, ensure the player is always player1 side
    let monsterId = p1.monsterId;
    let enemyMonsterId = p2.monsterId;
    let playerIsP1 = true;

    if (!isSpectator && p2.isPlayer) {
      // Swap so player is always on left side
      monsterId = p2.monsterId;
      enemyMonsterId = p1.monsterId;
      playerIsP1 = false;
    }

    // カスタムAI設定を取得
    const playerParticipant = p1.isPlayer ? p1 : p2;
    const playerAIConfig = isSpectator ? p1.aiConfig : playerParticipant.aiConfig;
    const enemyAIConfig = isSpectator ? p2.aiConfig : undefined;

    this.transitionTo(SceneKey.BATTLE, {
      monsterId,
      enemyMonsterId,
      aiLevel: this.tournamentState.aiLevel,
      enemyAiLevel: isSpectator ? this.tournamentState.aiLevel : undefined,
      mode: gameMode,
      tournamentState: this.tournamentState,
      tournamentMatchIndex: match.matchIndex,
      tournamentPlayerIsP1: playerIsP1,
      playerAIConfig,
      enemyAIConfig,
    });
  }

  private skipMatch(match: TournamentMatch): void {
    if (!match.participant1Id || !match.participant2Id) return;
    const p1 = this.tournamentState.participants.find((p) => p.id === match.participant1Id);
    const p2 = this.tournamentState.participants.find((p) => p.id === match.participant2Id);
    if (!p1 || !p2) {
      // 参加者が見つからない場合は強制的にparticipant1の勝利として進める
      const winnerId = match.participant1Id || null;
      const loserId = match.participant2Id || null;
      if (winnerId) {
        this.tournamentState = recordMatchResult(this.tournamentState, match.matchIndex, {
          winnerId,
          loserId,
          battleResult: BattleResultType.PLAYER1_WIN,
        });
        if (this.tournamentState.mode === TournamentMode.SINGLE_ELIMINATION) {
          this.tournamentState = advanceEliminationBracket(this.tournamentState, match.matchIndex);
        }
        this.renderUI();
      }
      return;
    }

    const m1 = FINAL_MONSTER_DATABASE.find((m) => m.id === p1.monsterId);
    const m2 = FINAL_MONSTER_DATABASE.find((m) => m.id === p2.monsterId);
    if (!m1 || !m2) return;

    const battleResult = simulateHeadlessBattle(m1, m2, this.tournamentState.aiLevel, p1.aiConfig, p2.aiConfig);

    let winnerId: string | null = null;
    let loserId: string | null = null;
    if (battleResult === BattleResultType.PLAYER1_WIN) {
      winnerId = match.participant1Id;
      loserId = match.participant2Id;
    } else if (battleResult === BattleResultType.PLAYER2_WIN) {
      winnerId = match.participant2Id;
      loserId = match.participant1Id;
    }

    this.tournamentState = recordMatchResult(this.tournamentState, match.matchIndex, {
      winnerId,
      loserId,
      battleResult,
    });

    if (this.tournamentState.mode === TournamentMode.SINGLE_ELIMINATION) {
      this.tournamentState = advanceEliminationBracket(this.tournamentState, match.matchIndex);
    }

    this.renderUI();
  }
}
